(ns app.front.view.group.admin.integration.back-end-system
  (:require [app.domain.group.config.integration-model :as integration-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-db ::start-editing
  (fn [db [_ group-id attribute default-value]]
    (assoc-in db [::editor-values group-id attribute] default-value)))

(rf/reg-event-db ::set-editor-value
  (fn [db [_ group-id attribute k v]]
    (assoc-in db [::editor-values group-id attribute k] v)))

(rf/reg-event-db ::cancel-editing
  (fn [db [_ group-id attribute]]
    (p/dissoc-in db [::editor-values group-id attribute])))

(rf/reg-event-fx ::save-edits
  (fn [{:keys [db]} [_ group-id attribute]]
    {:comm/command {:command    :group/save-integration-mapping
                    :data       {:group-id group-id
                                 :data     (->> (get-in db [::editor-values group-id attribute])
                                                (p/remove-vals str/blank?)
                                                (hash-map attribute))}
                    :on-success [::save-edits.success group-id attribute]
                    :notify     {:success :changes-saved}}}))

(rf/reg-event-fx ::save-edits.success
  (fn [_ [_ group-id attribute status resp]]
    {:fx [[:dispatch [::cancel-editing group-id attribute]]
          [:dispatch [::entities/apply-changes status resp]]]}))

(rf/reg-sub ::editor-values :-> ::editor-values)

(rf/reg-sub ::group-editor-values
  :<- [::editor-values]
  :<- [::gas/group-id]
  (fn [[editor-values group-id] _]
    (get editor-values group-id)))

(rf/reg-sub ::editor-value
  :<- [::group-editor-values]
  (fn [group-values [_ attribute]]
    (get group-values attribute)))

(rf/reg-sub ::party-roles
  :<- [::gas/party-roles]
  :<- [:language]
  (fn [[party-roles lang]]
    (->> party-roles
         (map (fn [{:party-role/keys [names key]}]
                {:role-key key
                 :label    (i18n-model/get-text-from-entities-or-map names lang)}))
         (sort-by :label))))

(defn table-section [{:keys [tr
                             group
                             items
                             guide
                             attribute
                             key-tr-fn
                             key-col-title
                             value-col-title]}]
  (let [mapping-in-editor (<sub [::editor-value attribute])
        editing?          (boolean mapping-in-editor)
        mapping           (or mapping-in-editor
                              (attribute group))
        group-id          (:db/id group)]
    [section/section
     (str "integration-config-" (name key-col-title))
     {:tr             tr
      :heading-button (cc/button {:size             :small
                                  :kind             :ghost
                                  :icon-description ""
                                  :disabled         editing?
                                  :on-click         #(>evt [::start-editing
                                                            group-id
                                                            attribute
                                                            (or (select-keys mapping items)
                                                                {})])}
                                 (tr [:edit]))}
     attribute
     [:div
      [:p guide]
      [:div.w-full.lg:w-2-3
       (if (:group/id group)
         [ui-table/modern-responsive-table
          {:items   items
           :id-func identity
           :cols    [[key-col-title
                      (fn [k]
                        [:div.font-semibold (if key-tr-fn
                                              (key-tr-fn k)
                                              (tr [k]))])
                      {}]
                     [value-col-title
                      (fn [k]
                        (let [v (get mapping k)]
                          [:div.font-mono
                           (if editing?
                             [ui/text-input {:value     v
                                             :on-change #(>evt [::set-editor-value
                                                                group-id
                                                                attribute
                                                                k
                                                                (futil/tval %)])}]
                             v)]))
                      {}]]
           :tr      tr}]
         (cc/skeleton-text {:paragraph true}))]
      (when editing?
        [:div.flex.justify-end.mt-3
         (cc/button {:kind       :ghost
                     :class-name "mr-2"
                     :on-click   #(>evt [::cancel-editing group-id attribute])}
                    (tr [:cancel]))
         (cc/button {:on-click #(>evt [::save-edits group-id attribute])}
                    (tr [:save]))])]]))

(defn status-mapping [{:keys [tr group backend-system-name]}]
  [table-section (util/mmap {:items           integration-model/integration-permit-statuses
                             :attribute       :group/integration-state-mapping
                             :guide           (tr [:integration-config/cloudpermit-status-guide]
                                                  [backend-system-name])
                             :key-col-title   :integration-config/cloudpermit-status
                             :value-col-title (tr [:integration-config/integration-status-code]
                                                  [backend-system-name])}
                            tr
                            group)])

(defn party-role-mapping [{:keys [tr group backend-system-name]}]
  (let [party-roles (<sub [::party-roles])]
    [table-section (util/mmap {:items           (map :role-key party-roles)
                               :attribute       :group/integration-party-role-mapping
                               :guide           (tr [:integration-config/party-role-guide]
                                                    [backend-system-name])
                               :key-col-title   :integration-config/party-role
                               :value-col-title (tr [:integration-config/integration-party-role-code]
                                                    [backend-system-name])
                               :key-tr-fn       #(-> party-roles
                                                     (p/find-first {:role-key %})
                                                     :label)}
                              tr
                              group)]))

(defn workspace-attrs-mapping [{:keys [tr group backend-system-name]}]
  [table-section (util/mmap {:items           integration-model/mappable-workspace-attrs
                             :attribute       :group/integration-workspace-attr-mapping
                             :guide           (tr [:integration-config/workspace-attr-guide]
                                                  [backend-system-name])
                             :key-col-title   :integration-config/workspace-attr
                             :value-col-title (tr [:integration-config/integration-field-code]
                                                  [backend-system-name])}
                            tr
                            group)])

(defn view [ctx]
  (let [backend-system-name (<sub [::gas/backend-system-name])
        ctx                 (merge ctx {:backend-system-name backend-system-name})]
    [:<>
     [status-mapping ctx]
     [party-role-mapping ctx]
     [workspace-attrs-mapping ctx]]))
